import React, { useEffect, useRef, useState } from "react";
import i18next from "i18next";
import { useMapContext } from "../../Contexts/MapContext";
import { getDrawingLayer, zoomToExtent } from "../mapFunctions";
import { Feature } from "ol";
import { DrawType, DrawingSection } from "../enums";
import { formatArea, formatLength } from "../Symbols/measureSymbols";
import { unzipFile } from "./zipUtils";
import { getGpxFeatures } from "./gpx";
import { extractStylesFromKML, getColorFromKmlStyles, getKmlFeatures } from "./kml";
import { AccordionItem } from "../../Elements/Accordion";
import { extend, Extent, buffer } from "ol/extent";
import { useDrawingContext } from "../../Contexts/DrawingContext";
import "./Import.scss";

const acceptTypes = [
    "application/vnd.google-earth.kml+xml",
    "application/vnd.google-earth.kmz",
    "application/gpx+xml",
    ".gpx",
    ".kml",
    ".kmz"
];
const allowedTypes = new Set(acceptTypes);

export const Import = (): JSX.Element => {
    const { drawnFeatures, setDrawnFeatures, openDrawnFeatures } = useMapContext();
    const { toolsExpanded, setToolExpanded } = useDrawingContext();
    const [uploadList, setUploadList] = useState<FileList>();
    const inputRef = useRef();

    useEffect(() => {
        uploadList && uploadList[0] && readFile(uploadList[0]);
    }, [uploadList]);

    const readFile = async (inputFile: File) => {
        const featureArray: Feature[] = [];
        const nameParts = inputFile.name.split(".");
        let extension = nameParts[nameParts.length - 1];

        let file;
        if (extension === "kmz") {
            file = await unzipFile(inputFile).then((unzippedFiles) => unzippedFiles && unzippedFiles[0]);
            extension = "kml";
        } else {
            file = inputFile;
        }
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = (e) => onFileLoaded(e, featureArray, extension);
        reader.onloadend = () => {
            const drawn = [...drawnFeatures, ...featureArray];
            setDrawnFeatures(drawn);
            getDrawingLayer().getSource().addFeatures(drawn);
            zoomToExtent(
                featureArray.reduce<Extent>(
                    (result, feature) => (result === null ? getBuffer(feature) : extend(result, getBuffer(feature))),
                    null
                ),
                () => {
                    setUploadList(null);
                    openDrawnFeatures();
                }
            );
        };
    };

    const onFileLoaded = async (event: ProgressEvent<FileReader>, featureArray: Feature[], extension: string) => {
        const features =
            extension === "kml"
                ? getKmlFeatures(event.target.result)
                : extension === "gpx"
                ? getGpxFeatures(event.target.result)
                : [];

        const styles = extension === "kml" && extractStylesFromKML(event.target.result);

        features.map((f) => addProperties(f, styles));
        featureArray.push(...features);
    };

    const onAdd = (e) => {
        const selectedFiles: FileList = e.target.files;
        if (!selectedFiles.length || !inputRef.current) {
            return;
        }

        const dt = new DataTransfer();

        const addFile = (file: File) => {
            if (!file.type) {
                dt.items.add(addFileType(file));
                return;
            }
            if (!allowedTypes.has(file.type)) {
                e.preventDefault();
                return;
            }
            dt.items.add(file);
        };

        if (uploadList && uploadList.length) {
            Array.from(uploadList).map((file) => {
                addFile(file);
            });
        }

        Array.from(selectedFiles).map((file) => addFile(file));

        onChangeList(dt.files);
    };

    const onChangeList = (list: FileList) => {
        (inputRef.current as HTMLInputElement).files = list;
        setUploadList(list);
    };

    return (
        <AccordionItem
            title={i18next.t("sketch.importGeometry")}
            changeHeight={Math.random()}
            key="geometryImport"
            defaultExpanded={toolsExpanded.includes(DrawingSection.Import) ? Math.random() : null}
            onExpand={(exp: boolean) => setToolExpanded(DrawingSection.Import, exp)}
        >
            <div className="upload-button-container">
                <div className="upload-button">
                    <label className="upload-button-label" htmlFor="attachments">
                        <a role="button" aria-disabled="false">
                            + Importēt
                        </a>
                    </label>
                    <input
                        ref={inputRef}
                        className="file-input-element"
                        type="file"
                        id="attachments"
                        name="attachments"
                        onChange={onAdd}
                        // multiple
                        accept={acceptTypes.join(",")}
                    />
                </div>
            </div>
        </AccordionItem>
    );
};

const addProperties = (feature: Feature, styles) => {
    const oid = Math.random() * 1234567;
    const drawType = feature.get("DRAWTYPE") || getDrawType(feature);
    const properties = {
        TEMPID: oid,
        DESCRIPTION: feature.get("DESCRIPTION") || getDescription(feature),
        DRAWTYPE: drawType,
        MEASUREMENTSHIDDEN: feature.get("MEASUREMENTSHIDDEN") === "true",
        COLOR: getColorFromKmlStyles(feature, styles)
    };

    if (drawType === DrawType.Polyline) {
        properties["LENGTH"] = feature.get("LENGTH") || formatLength(feature.getGeometry());
    }
    if (drawType === DrawType.Polygon) {
        properties["AREA"] = feature.get("AREA") || formatArea(feature.getGeometry());
        properties["LENGTH"] = feature.get("LENGTH") || formatLength(feature.getGeometry());
    }

    feature.setId(oid);
    feature.setStyle();
    feature.setProperties(properties);
};

const addFileType = (file: File): File => {
    const extension = file.name.split(".")[1].toLowerCase();
    const type =
        extension === "kml"
            ? "application/vnd.google-earth.kml+xml"
            : extension === "kmz"
            ? "application/vnd.google-earth.kmz"
            : extension === "gpx"
            ? "application/gpx+xml"
            : "";
    console.log("extension", extension, type);
    return new File([file], file.name, {
        type
    });
};

const getDrawType = (feature: Feature): DrawType => {
    const type = feature.getGeometry().getType();
    return type === "Polygon" || type === "MultiPolygon"
        ? DrawType.Polygon
        : type === "LineString" || type === "MultiLineString"
        ? DrawType.Polyline
        : type === "Point"
        ? getDescription(feature)
            ? DrawType.PointText
            : DrawType.Point
        : null;
};

const getDescription = (feature: Feature): string => {
    const description = feature.get("description");
    return description && description !== feature.get("name") ? description : undefined;
};

const getBuffer = (feature: Feature) => buffer(feature.getGeometry().getExtent(), 100);
